import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Drawer,
  InputAdornment,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  GetSubScriptionList,
  GetSubscriptionById,
  ISubscription,
} from "../../services/SubscriptionServices";
import { TableBodySubsciptionList } from "../../utils/ContentCardList";
import { useSnackbar } from "notistack";
import { AssignSubscription } from "../../services/MembersService";
import { SubscriptionThumbCard } from "../../components/ContentCard";
import {
  calculateDiscountedPrice,
  capitalizeFirstLetter,
} from "../../utils/commons";

interface DrawerAssignSubscriptionProps {
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  memberId: string;
}

const DrawerAssignSubscription: React.FC<DrawerAssignSubscriptionProps> = ({
  open,
  onClose,
  onUpdate,
  memberId,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState("");

  const handleAssignSubscription = async (id: string): Promise<number> => {
    // Create a new banner object with the updated destination
    const status = await AssignSubscription({
      memberID: memberId,
      subscriptionId: id,
    });
    if (status === 200) {
      onUpdate();
      onClose();
      enqueueSnackbar("Subscription Assigned Successfully!", {
        variant: "success",
      });
      return 200;
    } else {
      enqueueSnackbar("Failed to Assign Subscription!", {
        variant: "warning",
      });
      return 401;
    }
  };

  const handleConfirmAssign = async (id: string): Promise<number> => {
    setSelectedSubscriptionId(id);
    setConfirmDialogOpen(true);
    return 0;
  };

  const confirmAssign = () => {
    handleAssignSubscription(selectedSubscriptionId);
    setConfirmDialogOpen(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: theme.palette.background.paper,
          borderTopLeftRadius: theme.spacing(2),
          borderBottomLeftRadius: theme.spacing(2),
          width: "30%",
          height: "100vh",
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        component="form"
        aria-label="Video List"
        sx={{
          display: "flex",
          backgroundColor: theme.palette.background.paper,
          flexDirection: "column",
          height: "100vh",
          maxHeight: "100vh",
          boxSizing: "border-box",
        }}
      >
        <Box>
          {/* Your subscription list component */}
          {/* Assuming it renders a list of subscriptions */}
          {/* Replace with your actual implementation */}
          <SubscriptionList
            onSelect={handleConfirmAssign}
            title="Choose Subscription"
          />
        </Box>
      </Box>

      <DialogContainer
        dailogopen={confirmDialogOpen}
        setDialogOpen={setConfirmDialogOpen}
        onConfirm={confirmAssign}
        contentId={selectedSubscriptionId}
      />
    </Drawer>
  );
};

export default DrawerAssignSubscription;

const SubscriptionList = ({
  onSelect,
  title,
}: {
  onSelect: (id: string) => Promise<number>;
  title: string;
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const rowsPerPageOptions = [5, 15, 25];
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [page, setPage] = useState<number>(0);

  useEffect(() => {
    fetchSubscriptions();
  }, [page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await GetSubScriptionList({ page, size: 10 });
      setSubscriptions(data.content);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: theme.palette.background.paper,
        flexDirection: "column",
        height: "100vh",
        minHeight: "100vh",
        maxHeight: "100vh",
        padding: theme.spacing(1),
        boxSizing: "border-box",
      }}
    >
      <Box
        mb={2}
        mt={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">
          <b style={{ color: "gray", paddingLeft: 5, cursor: "none" }}>
            {title}
          </b>
        </Typography>
        <TextField
          placeholder="Search by name"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          marginBottom: theme.spacing(1),
          scrollbarWidth: "none",
        }}
      >
        <TableBodySubsciptionList
          drawer
          loading={loading}
          Subscriptions={subscriptions}
          callBackchange={onSelect}
        />
      </Box>
      <Box sx={{ marginTop: "auto" }}>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

const DialogContainer = ({
  dailogopen,
  setDialogOpen,
  onConfirm,
  contentId,
}: {
  dailogopen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm: () => void;
  contentId: string;
}) => {
  const [SubscriptionData, setSubscriptionData] = useState<ISubscription>();

  useEffect(() => {
    if (contentId) {
      fetchContent(contentId);
    }
  }, [contentId]);

  const fetchContent = async (id: string) => {
    const data = await GetSubscriptionById({ id });
    if (data) {
      setSubscriptionData(data);
    }
  };

  return (
    <>
      <Dialog open={dailogopen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirm Subscription Assignment</DialogTitle>

        <DialogContent>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 20,
            }}
          >
            {/* Left side: Subscription thumbnail */}
            <Box style={{ flex: 1.5 }}>
              {SubscriptionData?.thumbnailUrl && (
                <SubscriptionThumbCard
                  srcUrl={SubscriptionData?.thumbnailUrl}
                  width={220}
                />
              )}
            </Box>

            {/* Right side: Subscription details */}
            <Box style={{ flex: 3.5, width: 350 }}>
              <Typography variant="h6">
                {capitalizeFirstLetter(SubscriptionData?.name as string)}
              </Typography>
              <Typography variant="body2">
                {SubscriptionData?.shortDescription &&
                SubscriptionData?.shortDescription.length > 90
                  ? SubscriptionData?.shortDescription.slice(0, 90) + "..."
                  : SubscriptionData?.shortDescription}
              </Typography>
              <Typography variant="body1" mt={1}>
                Duration: <b>{SubscriptionData?.duration} days</b>
              </Typography>
              <Typography variant="body1">
                Start At: <b>{new Date().toDateString()}</b>
              </Typography>
              {/* <Typography variant="body2">
                Start Date: {SubscriptionData?.}
              </Typography>
              <Typography variant="body2">
                End Date: {SubscriptionData?.updatedAt}
              </Typography> */}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            margin: 2,
            marginTop: 0,
          }}
        >
          <Typography variant="body2" sx={{ width: "66%" }}>
            <span style={{ color: "gray" }}>
              By Confirming, this program will be assigned to the customer and a
              transaction of{" "}
              <span style={{ color: "green" }}>
                {" "}
                <b>
                  {"\u20B9"}{" "}
                  {calculateDiscountedPrice(
                    SubscriptionData?.price as number,
                    SubscriptionData?.discount as number
                  )}{" "}
                </b>{" "}
              </span>{" "}
              will be recorded as cash payment in the system for todays date.
            </span>
          </Typography>
          <Box>
            <Button onClick={() => setDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button onClick={onConfirm} color="primary">
              Confirm
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
